import React, { useState, useEffect, useCallback, useMemo } from 'react'
import api from '../utils/api'
import {
  Layout,
  Drawer,
  Form,
  Input,
  InputNumber,
  DatePicker,
  Select,
  Switch,
  message,
  Typography,
  Space,
  Tabs,
  Table,
  Empty,
  Tag,
  Divider,
  Row,
  Col,
  Button,
  Modal,
  Radio
} from 'antd'
import styled from 'styled-components'
import { useAuth } from '../hooks/AuthContext'
import { useMediaQuery } from 'react-responsive'
import ResponsiveSelect from '../components/common/ResponsiveSelect'
import Hint from '../components/common/Hint'
import PointsSettings from '../components/PointsSettings'
import { useMembership } from '../hooks/useMembership'
import { usePermission } from '../hooks/usePermission'
import { ExclamationCircleOutlined } from '@ant-design/icons'
import { formatTz } from '../utils/timezone'
import { now as tzNow } from '../utils/timezone'

/** 舊版寫死的英文等級值 → 中文顯示；店家自訂的等級直接顯示原名 */
const LEGACY_TIER_LABELS = {
  Regular: '普通會員',
  Silver: '銀卡會員',
  Gold: '金卡會員',
  Diamond: '鑽石會員',
  VIP: 'VIP會員',
  Vip: 'VIP會員'
}

const { Content } = Layout
const { Text } = Typography

const StyledContent = styled(Content)`
  padding: 24px;
  @media (max-width: 768px) {
    padding: 16px;
  }
`
const StyledButton = styled(Button)`
  width: 100%;
  height: 50px;
  font-size: 16px;
`

const StyledDrawer = styled(Drawer)`
  .ant-drawer-body {
    padding: 24px;
  }
`
const DetailText = styled(Typography.Text)`
  cursor: pointer;
  margin-left: 5px;
  font-size: 14px;
  font-weight: 500;
  color: #3e3d3c;
`

const DrawerSection = styled.div`
  margin-bottom: 24px;
`

const DrawerTitle = styled(Typography.Title)`
  font-size: 16px !important;
  margin-bottom: 16px !important;
`

const DrawerContent = styled(Typography.Paragraph)`
  margin-bottom: 8px;
`
/** 表單欄位下方的灰色小字說明（不是警示區塊，警示請用 common/Hint） */
const InlineNote = styled.div`
  margin-bottom: 5px;
  font-size: 14px;
  color: #999;
`

const CouponPage = () => {
  const [coupons, setCoupons] = useState([])
  const [products, setProducts] = useState([])
  const [form] = Form.useForm()
  const discountTypeWatch = Form.useWatch('discountType', form) || 'fixed'
  const [isDrawerVisible, setIsDrawerVisible] = useState(false)
  const [selectedCoupon, setSelectedCoupon] = useState(null)
  const [activeTab, setActiveTab] = useState('1')
  const { businessId } = useAuth()
  const isMobile = useMediaQuery({ maxWidth: 768 })
  const { hasFeature } = useMembership()
  const { can, canAny } = usePermission()
  // 紅利點數分頁：方案有包含才顯示；沒有 points:settings 的人唯讀
  const showPointsTab = hasFeature('points') &&
    canAny(['points:view', 'points:manage', 'points:settings'])
  const [applicableUserType, setApplicableUserType] = useState(null)
  const [selectedProducts, setSelectedProducts] = useState([])
  /** 全部票券：依到期日排序 — expiry_asc 早→晚、expiry_desc 晚→早、none 後端原始順序 */
  const [couponListSort, setCouponListSort] = useState('expiry_asc')
  /**
   * 店家自己的會員等級。優惠券的「最低會員等級」以前寫死 5 個英文值
   * （Regular/Silver/Gold/Diamond/Vip），跟店家實際在用的等級名稱對不上，
   * 結帳端比不到就當成最低級 —— 券看起來有門檻，其實人人可用。
   * 沒設定過等級的店維持舊的英文清單，既有的券不受影響。
   */
  const [membershipTiers, setMembershipTiers] = useState([])

  const fetchCoupons = useCallback(async () => {
    try {
      const response = await api.get(`/api/business_B/${businessId}/coupons`)
      if (Array.isArray(response.data)) {
        setCoupons(response.data)
      } else if (response.data && Array.isArray(response.data.data)) {
        setCoupons(response.data.data)
      } else {
        console.error('Unexpected data format:', response.data)
        setCoupons([])
      }
    } catch (error) {
      console.error('獲取優惠券失敗:', error)
      setCoupons([])
    }
  }, [businessId])

  const fetchMembershipTiers = useCallback(async () => {
    try {
      const response = await api.get(
        `/api/business_B/${businessId}/membership-tiers`
      )
      const tiers = response.data?.data?.tiers || []
      setMembershipTiers(
        tiers
          .filter((tier) => tier && tier.isEnabled !== false)
          .sort((a, b) => (a.order || 0) - (b.order || 0))
      )
    } catch (error) {
      console.error('獲取會員等級失敗:', error)
      setMembershipTiers([])
    }
  }, [businessId])

  const fetchProducts = useCallback(async () => {
    try {
      const response = await api.get(`/api/business_B/${businessId}/products`)
      setProducts(response.data)
    } catch (error) {
      console.error('獲取產品列表失敗:', error)
    }
  }, [businessId])

  useEffect(() => {
    fetchCoupons()
    fetchProducts()
    fetchMembershipTiers()
  }, [businessId, fetchCoupons, fetchProducts, fetchMembershipTiers])

  const handleAddCoupon = async (values) => {
    try {
      // 處理 applicableProducts
      if (
        values.applicableProducts &&
        values.applicableProducts.includes('all')
      ) {
        values.applicableProducts = products.map((product) => product._id)
      }

      await api.post(`/api/business_B/${businessId}/coupons`, values)

      message.success('優惠券添加成功')
      form.resetFields()
      fetchCoupons()
    } catch (error) {
      console.error('添加優惠券失敗:', error)

      if (error.response && error.response.data) {
        const { error: errorMessage, nonExistingEmails } = error.response.data

        if (
          errorMessage === '以下電子郵件地址在平臺中不存在' &&
          nonExistingEmails
        ) {
          message.error(`${errorMessage}: ${nonExistingEmails.join(', ')}`)
        } else {
          message.error(errorMessage || '添加優惠券失敗')
        }
      } else {
        message.error('添加優惠券失敗')
      }
    }
  }

  const showDrawer = (coupon) => {
    setSelectedCoupon(coupon)
    setIsDrawerVisible(true)
  }

  const closeDrawer = () => {
    setIsDrawerVisible(false)
    setSelectedCoupon(null)
  }

  const handleIssueCoupon = async (couponId, isActive) => {
    try {
      await api.put(
        `/api/business_B/${businessId}/coupons/${couponId}/status`,
        {
          isActive
        }
      )
      message.success(isActive ? '優惠券已啟用' : '優惠券已停用')
      fetchCoupons()
    } catch (error) {
      console.error('更新優惠券狀態失敗:', error)
      message.error('更新優惠券狀態失敗', error.message)
    }
  }

  const getProductName = (productId) => {
    const product = products.find((p) => p._id === productId)
    return product ? product.name : '未知產品'
  }

  const handleProductSelect = (value) => {
    if (value.includes('all')) {
      setSelectedProducts(products.map((product) => product._id))
    } else {
      setSelectedProducts(value)
    }
  }

  const validateCouponCode = (_, value) => {
    if (!value) {
      return Promise.reject('請輸入優惠券代碼')
    }
    if (!/^[A-Za-z0-9]{1,10}$/.test(value)) {
      return Promise.reject(
        '優惠券代碼只能包含英文字母和數字，且不超過10個字符'
      )
    }
    return Promise.resolve()
  }

  const validateDiscount = (_, value) => {
    const dtype = form.getFieldValue('discountType') || 'fixed'
    const minimumPurchase = form.getFieldValue('minimumPurchase')
    if (dtype === 'percent') {
      if (value === undefined || value === null || value === '') {
        return Promise.reject('請輸入折扣趴數')
      }
      const n = Number(value)
      if (Number.isNaN(n) || n <= 0 || n > 100) {
        return Promise.reject('趴數須介於 1～100')
      }
      return Promise.resolve()
    }
    if (value > minimumPurchase) {
      return Promise.reject('折扣金額不能大於最低消費金額')
    }
    return Promise.resolve()
  }

  const formatCouponDiscountLabel = (coupon) => {
    if (!coupon || coupon.discount == null) return '-'
    const t = coupon.discountType || 'fixed'
    if (t === 'percent') return `${coupon.discount}%`
    return `NT$ ${coupon.discount}`
  }

  const getTargetAudienceInChinese = (targetAudience) => {
    const audienceMap = {
      newUser: '新用戶',
      firstConsumption: '首次消費',
      allUsers: '所有用戶',
      membershipLevel: '會員等級限制',
      specifiedUsers: '指定用戶',
      birthdayUsers: '生日用戶'
    }
    return audienceMap[targetAudience] || targetAudience
  }

  const validateEmails = (_, value) => {
    if (!value || value.length === 0) {
      return Promise.reject('請至少輸入一個電子郵件地址')
    }
    const invalidEmails = value.filter(
      (email) => !email.match(/^[^\s@]+@[^\s@]+\.[^\s@]+$/)
    )
    if (invalidEmails.length > 0) {
      return Promise.reject(`以下電子郵件地址無效: ${invalidEmails.join(', ')}`)
    }
    return Promise.resolve()
  }

  const handleDeleteCoupon = async (couponId) => {
    try {
      await api.delete(`/api/business_B/${businessId}/coupons/${couponId}`)
      message.success('優惠券刪除成功')
      fetchCoupons() // 重新獲取優惠券列表
    } catch (error) {
      console.error('刪除優惠券失敗:', error)
      message.error('刪除優惠券失敗')
    }
  }

  const showDeleteConfirm = (couponId) => {
    Modal.confirm({
      title: '確定要刪除這個優惠券嗎?',
      icon: <ExclamationCircleOutlined />,
      content: '刪除後將無法恢復',
      okText: '確定',
      okType: 'danger',
      cancelText: '取消',
      onOk() {
        handleDeleteCoupon(couponId)
      }
    })
  }

  /**
   * 有設等級表就用店家自己的等級（由低到高），沒有才退回舊的英文清單。
   * 送出的 value 必須跟 Business.membershipTiers.name 一字不差，
   * 結帳時 membershipMeets 是用 order 比大小的。
   */
  const membershipLevelOptions = useMemo(() => {
    if (membershipTiers.length > 0) {
      return membershipTiers.map((tier) => ({
        value: tier.name,
        label: tier.name
      }))
    }
    return [
      { value: 'Regular', label: '普通會員' },
      { value: 'Silver', label: '銀卡會員' },
      { value: 'Gold', label: '金卡會員' },
      { value: 'Diamond', label: '鑽石會員' },
      { value: 'VIP', label: 'VIP會員' }
    ]
  }, [membershipTiers])

  const renderCouponForm = () => (
    <Form
      form={form}
      layout='vertical'
      onFinish={handleAddCoupon}
      initialValues={{ discountType: 'fixed' }}
    >
      <Form.Item name='discountType' label='折扣方式'>
        <Radio.Group>
          <Radio value='fixed'>固定金額（元）</Radio>
          <Radio value='percent'>趴數折扣（%）</Radio>
        </Radio.Group>
      </Form.Item>
      {discountTypeWatch === 'percent' && (
        <InlineNote style={{ marginTop: -12, marginBottom: 12 }}>
          例如填 10，表示從訂單金額折掉 10%。
        </InlineNote>
      )}
      <Row gutter={16}>
        <Col span={12}>
          <Form.Item
            name='code'
            label='優惠券代碼'
            rules={[
              { required: true, message: '請輸入優惠券代碼' },
              { validator: validateCouponCode }
            ]}
          >
            <Input placeholder='輸入優惠券代碼' />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item
            name='discount'
            label={
              discountTypeWatch === 'percent'
                ? '折扣趴數（%）'
                : '折扣金額（元）'
            }
            dependencies={['discountType', 'minimumPurchase']}
            rules={[
              {
                required: true,
                message:
                  discountTypeWatch === 'percent'
                    ? '請輸入折扣趴數'
                    : '請輸入折扣金額'
              },
              ...(discountTypeWatch === 'percent'
                ? [
                    {
                      type: 'number',
                      min: 1,
                      max: 100,
                      message: '趴數須介於 1～100'
                    }
                  ]
                : [
                    {
                      type: 'number',
                      min: 0,
                      message: '折扣金額不能為負數'
                    }
                  ]),
              { validator: validateDiscount }
            ]}
          >
            <InputNumber
              min={discountTypeWatch === 'percent' ? 1 : 0}
              max={discountTypeWatch === 'percent' ? 100 : undefined}
              style={{ width: '100%' }}
              addonAfter={discountTypeWatch === 'percent' ? '%' : undefined}
            />
          </Form.Item>
        </Col>
      </Row>
      <Row gutter={16}>
        <Col span={12}>
          <Form.Item
            name='expiryDate'
            label='截止日期'
            rules={[
              { required: true, message: '請選擇截止日期' },
              {
                type: 'date',
                min: tzNow().toDate(),
                message: '截止日期不能早於今天'
              }
            ]}
          >
            <DatePicker style={{ width: '100%' }} placeholder='選擇到期日' />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item
            name='totalQuantity'
            label='總數量'
            rules={[
              { required: true, message: '請輸入總數量' },
              { type: 'number', min: 1, message: '總數量必須大於0' }
            ]}
          >
            <InputNumber min={1} style={{ width: '100%' }} />
          </Form.Item>
        </Col>
      </Row>
      <Row gutter={16}>
        <Col span={12}>
          <Form.Item
            name='minimumPurchase'
            label='最低消費金額'
            rules={[
              { required: true, message: '請輸入最低消費金額' },
              { type: 'number', min: 0, message: '最低消費金額不能為負數' }
            ]}
          >
            <InputNumber min={0} style={{ width: '100%' }} />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item name='isActive' label='是否啟用' valuePropName='checked'>
            <Switch />
          </Form.Item>
        </Col>
      </Row>
      <Form.Item name='description' label='描述'>
        <Input.TextArea placeholder='輸入優惠券描述' />
      </Form.Item>
      <Form.Item name='applicableProducts' label='適用服務'>
        <Select
          mode='multiple'
          placeholder='選擇適用服務'
          value={selectedProducts}
          onChange={handleProductSelect}
        >
          <Select.Option key='all' value='all'>
            全部
          </Select.Option>
          {products.map((product) => (
            <Select.Option key={product._id} value={product._id}>
              {product.name}
            </Select.Option>
          ))}
        </Select>
      </Form.Item>
      <Form.Item name='targetAudience' label='適用人群' required>
        <Select
          placeholder='選擇適用人群'
          onChange={(value) => setApplicableUserType(value)}
        >
          <Select.Option value='newUser'>新用戶</Select.Option>
          <Select.Option value='firstConsumption'>首次消費</Select.Option>
          <Select.Option value='allUsers'>所有用戶</Select.Option>
          <Select.Option value='membershipLevel'>會員等級限制</Select.Option>
          <Select.Option value='specifiedUsers'>指定用戶</Select.Option>
          <Select.Option value='birthdayUsers'>生日用戶</Select.Option>
        </Select>
      </Form.Item>
      <InlineNote>
        除指定用戶外，其他優惠券皆不會自動發送到用戶帳號，請自行通知!
      </InlineNote>

      {applicableUserType === 'specifiedUsers' && (
        <Form.Item
          name='specifiedUserEmails'
          label='指定用戶郵箱'
          required
          rules={[{ validator: validateEmails }]}
        >
          <InlineNote>
            請輸入指定用戶的電子郵件地址，每個地址以逗號分隔，此優惠券將會發送到這些用戶的帳號中，並請提供已註冊的會員郵件地址。
          </InlineNote>
          <Select
            mode='tags'
            style={{ width: '100%' }}
            placeholder='輸入電子郵件地址並按 Enter'
            tokenSeparators={[',']}
          />
        </Form.Item>
      )}

      {applicableUserType === 'membershipLevel' && (
        <Form.Item name='minimumMembershipLevel' label='最低會員等級' required>
          <ResponsiveSelect
            placeholder='選擇最低會員等級'
            options={membershipLevelOptions}
          />
        </Form.Item>
      )}

      <Form.Item>
        <StyledButton type='primary' htmlType='submit'>
          新增優惠券
        </StyledButton>
      </Form.Item>
    </Form>
  )

  const renderDiscountCoupon = () => <>{renderCouponForm()}</>

  const sortedCoupons = useMemo(() => {
    if (!Array.isArray(coupons) || coupons.length === 0) return []
    if (couponListSort === 'none') return [...coupons]
    const list = [...coupons]
    list.sort((a, b) => {
      const ta = new Date(a.expiryDate).getTime()
      const tb = new Date(b.expiryDate).getTime()
      const aOk = !Number.isNaN(ta)
      const bOk = !Number.isNaN(tb)
      if (!aOk && !bOk) return 0
      if (!aOk) return 1
      if (!bOk) return -1
      return couponListSort === 'expiry_asc' ? ta - tb : tb - ta
    })
    return list
  }, [coupons, couponListSort])

  const renderAllCoupons = () => {
    if (!Array.isArray(coupons) || coupons.length === 0) {
      return <Empty description='暫無優惠券數據' />
    }
    return (
      <>
        <Space
          direction={isMobile ? 'vertical' : 'horizontal'}
          align={isMobile ? 'stretch' : 'center'}
          wrap
          style={{
            marginBottom: 16,
            width: '100%'
          }}
        >
          <Text type='secondary'>依時間排序</Text>
          <Select
            value={couponListSort}
            onChange={setCouponListSort}
            style={{ width: isMobile ? '100%' : 260 }}
            options={[
              {
                value: 'expiry_asc',
                label: '到期日 · 近 → 遠（快過期在前）'
              },
              {
                value: 'expiry_desc',
                label: '到期日 · 遠 → 近（過期在前）'
              },
              { value: 'none', label: '後台原始順序' }
            ]}
          />
        </Space>
        <Table
          columns={isMobile ? mobileColumns : columns}
          dataSource={sortedCoupons}
          rowKey='_id'
          scroll={{ x: isMobile ? 300 : true }}
          pagination={{ simple: isMobile }}
        />
      </>
    )
  }

  const columns = [
    {
      title: '優惠券代碼',
      dataIndex: 'code',
      key: 'code'
    },
    {
      title: '折扣',
      key: 'discount',
      render: (_, record) => formatCouponDiscountLabel(record)
    },
    {
      title: '有效期至',
      dataIndex: 'expiryDate',
      key: 'expiryDate',
      // 到期日是絕對時刻：用裝置時區顯示會早一天，客人拿去對帳會吵架
      render: (text) => formatTz(text, 'YYYY/MM/DD')
    },
    {
      title: '是否啟用',
      dataIndex: 'isActive',
      key: 'isActive',
      render: (isActive, record) => (
        <Switch
          checked={isActive}
          onChange={(checked) => handleIssueCoupon(record._id, checked)}
        />
      )
    },
    {
      title: '操作',
      key: 'action',
      render: (_, record) => (
        <Space size='middle'>
          <Button type='link' onClick={() => showDrawer(record)}>
            詳情
          </Button>
          <Button type='link' onClick={() => showDeleteConfirm(record._id)}>
            刪除
          </Button>
        </Space>
      )
    }
  ]

  const mobileColumns = [
    {
      title: '優惠券',
      dataIndex: 'code',
      key: 'code',
      render: (text, record) => (
        <Space direction='vertical'>
          <Text strong>{text}</Text>
          <Text type='secondary'>
            折扣: {formatCouponDiscountLabel(record)}
          </Text>
          <Text type='secondary'>
            有效期至: {formatTz(record.expiryDate, 'YYYY/MM/DD')}
          </Text>
        </Space>
      )
    },
    {
      title: '操作',
      key: 'action',
      render: (_, record) => (
        <Space direction='vertical'>
          <Switch
            checked={record.isActive}
            onChange={(checked) => handleIssueCoupon(record._id, checked)}
          />
          <Button type='link' onClick={() => showDrawer(record)}>
            詳情
          </Button>
        </Space>
      )
    }
  ]

  const tabItems = [
    { key: '1', label: '新增優惠券', children: renderDiscountCoupon() },
    { key: '2', label: '全部票券', children: renderAllCoupons() },
    ...(showPointsTab
      ? [
          {
            key: '3',
            label: '紅利點數',
            children: (
              <PointsSettings
                businessId={businessId}
                canEdit={can('points:settings')}
              />
            )
          }
        ]
      : [])
  ]

  return (
    <Layout style={{ minHeight: '100vh' }}>
      <Layout>
        <StyledContent>
          <Tabs
            activeKey={activeTab}
            onChange={setActiveTab}
            items={tabItems}
            style={{ marginBottom: '24px' }}
          />
          <StyledDrawer
            title={<Typography.Title level={4}>優惠券詳情</Typography.Title>}
            placement='right'
            onClose={closeDrawer}
            open={isDrawerVisible}
            width={400}
          >
            {selectedCoupon && (
              <>
                <DrawerSection>
                  <DrawerTitle level={5}>基本信息</DrawerTitle>
                  <DrawerContent>
                    <strong>優惠券代碼:</strong> {selectedCoupon.code}
                  </DrawerContent>
                  <DrawerContent>
                    <strong>描述:</strong> {selectedCoupon.description}
                  </DrawerContent>
                  <DrawerContent>
                    <strong>折扣方式:</strong>{' '}
                    {(selectedCoupon.discountType || 'fixed') === 'percent'
                      ? '趴數折扣（%）'
                      : '固定金額（元）'}
                  </DrawerContent>
                  <DrawerContent>
                    <strong>折扣內容:</strong>{' '}
                    {formatCouponDiscountLabel(selectedCoupon)}
                  </DrawerContent>
                  <DrawerContent>
                    <strong>有效期至:</strong>{' '}
                    {formatTz(selectedCoupon.expiryDate, 'YYYY/MM/DD')}
                  </DrawerContent>
                </DrawerSection>

                <Divider />

                <DrawerSection>
                  <DrawerTitle level={5}>使用條件</DrawerTitle>
                  <DrawerContent>
                    <strong>總數量:</strong> {selectedCoupon.totalQuantity}
                  </DrawerContent>
                  <DrawerContent>
                    <strong>剩餘數量:</strong>{' '}
                    {selectedCoupon.remainingQuantity}
                  </DrawerContent>
                  <DrawerContent>
                    <strong>最低消費金額:</strong>{' '}
                    {selectedCoupon.minimumPurchase}
                  </DrawerContent>
                  <DrawerContent>
                    <strong>是否啟用:</strong>{' '}
                    {selectedCoupon.isActive ? '是' : '否'}
                  </DrawerContent>
                </DrawerSection>

                <Divider />

                <DrawerSection>
                  <DrawerTitle level={5}>適用服務</DrawerTitle>
                  <Space size={[0, 8]} wrap>
                    {selectedCoupon.applicableProducts.map((productId) => (
                      <Tag key={productId} color='blue'>
                        {getProductName(productId)}
                      </Tag>
                    ))}
                  </Space>
                </DrawerSection>

                <Divider />

                <DrawerSection>
                  <DrawerTitle level={5}>適用人群</DrawerTitle>
                  <DrawerContent>
                    <strong>適用人群:</strong>{' '}
                    {getTargetAudienceInChinese(selectedCoupon.targetAudience)}
                  </DrawerContent>
                  {selectedCoupon.targetAudience === 'membershipLevel' &&
                    membershipTiers.length > 0 &&
                    !membershipTiers.some(
                      (tier) =>
                        tier.name === selectedCoupon.minimumMembershipLevel
                    ) && (
                      <Hint tone='warning' title='這張券目前沒有人符合資格'>
                        門檻「{selectedCoupon.minimumMembershipLevel}
                        」不在店家的會員等級表裡，結帳時比不到對應等級。請編輯這張券，改選目前在用的等級。
                      </Hint>
                    )}
                  {selectedCoupon.targetAudience === 'membershipLevel' && (
                    <DrawerContent>
                      <strong>最低會員等級:</strong>{' '}
                      {LEGACY_TIER_LABELS[
                        selectedCoupon.minimumMembershipLevel
                      ] ||
                        selectedCoupon.minimumMembershipLevel ||
                        '（未設定）'}
                      （含）以上
                    </DrawerContent>
                  )}
                  {selectedCoupon.targetAudience === 'specifiedUsers' && (
                    <DrawerContent>
                      <strong>指定用戶:</strong>{' '}
                      {selectedCoupon.specifiedUserEmails.join(', ')}
                    </DrawerContent>
                  )}
                </DrawerSection>
              </>
            )}
          </StyledDrawer>
        </StyledContent>
      </Layout>
    </Layout>
  )
}

export default CouponPage
